import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CopyAll as CopyAllIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
} from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';

// 自定义图标组件
const WeChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.5 13.5C4.46 13.5 2 12.56 2 9.75C2 6.94 4.46 6 7.5 6C8.84 6 10.1 6.3 11.08 6.87L12 7.35L12.92 6.87C13.9 6.3 15.16 6 16.5 6C19.54 6 22 6.94 22 9.75C22 12.56 19.54 13.5 16.5 13.5H16V14C16 16.21 13.21 18 10 18C6.79 18 4 16.21 4 14V13.5H7.5Z" fill="#25D366"/>
  </svg>
);

const QQIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17C8.34 17 7 15.66 7 14C7 12.34 8.34 11 10 11C11.66 11 13 12.34 13 14C13 15.66 11.66 17 10 17ZM14 11C12.34 11 11 9.66 11 8C11 6.34 12.34 5 14 5C15.66 5 17 6.34 17 8C17 9.66 15.66 11 14 11Z" fill="#12B7F5"/>
  </svg>
);

const ZhihuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" fill="#0084FF"/>
    <path d="M12 8C10.9 8 10 8.9 10 10V14C10 15.1 10.9 16 12 16C13.1 16 14 15.1 14 14V10C14 8.9 13.1 8 12 8Z" fill="white"/>
  </svg>
);

const ShareDialog = ({ open, onClose, dramaTitle, dramaId, onShare }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shareUrl = `${window.location.origin}/drama/${dramaId}`;
  const shareText = `推荐精彩剧集：${dramaTitle}`;

  // 调用分享回调
  const handleShareComplete = (platform) => {
    if (onShare) {
      onShare(platform);
    }
  };

  // 复制链接到剪贴板
  const handleCopyLink = async () => {
    try {
      // 首先尝试使用现代浏览器的 clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
        alert('链接已复制到剪贴板');
        handleShareComplete('copy_link');
        return;
      }
      
      // 备用方案：使用传统的 document.execCommand 方法
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        alert('链接已复制到剪贴板');
        handleShareComplete('copy_link');
      } else {
        throw new Error('execCommand failed');
      }
    } catch (error) {
      console.error('复制链接失败:', error);
      // 最后方案：提示用户手动复制
      alert('复制链接失败，请手动复制');
    }
  };

  // 社交媒体分享函数
  const handleShareTo = (platform) => {
    let shareUrlPlatform = '';
    
    switch (platform) {
      case 'facebook':
        shareUrlPlatform = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        shareUrlPlatform = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        shareUrlPlatform = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      case 'telegram':
        shareUrlPlatform = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      case 'wechat':
        // 微信分享需要生成二维码，这里先复制链接到剪贴板，提示用户在微信中发送
        handleCopyLink();
        alert('链接已复制到剪贴板，请在微信中粘贴分享');
        return;
      case 'qq':
        shareUrlPlatform = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}&source=${encodeURIComponent('短剧播放平台')}`;
        break;
      case 'zhihu':
        // 知乎分享需要使用知乎的分享接口
        shareUrlPlatform = `https://zhuanlan.zhihu.com/write?edit=0&draft_id=0&title=${encodeURIComponent(shareText)}&content=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        return;
    }

    window.open(shareUrlPlatform, '_blank');
    handleShareComplete(platform);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: isMobile ? 1 : 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={isMobile ? 'h6' : 'h5'}>
          分享剧集
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        {/* 剧集信息 */}
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {dramaTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            分享给您的朋友
          </Typography>
        </Box>

        {/* 分享链接 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            分享链接
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={shareUrl}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <IconButton onClick={handleCopyLink} color="primary" size="small">
              <CopyAllIcon />
            </IconButton>
          </Box>
        </Box>

        {/* 社交分享 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            分享到社交媒体
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<FacebookIcon />}
              label="Facebook"
              size="medium"
              onClick={() => handleShareTo('facebook')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#4267B2',
                  color: 'white',
                },
              }}
            />
            <Chip
              icon={<TwitterIcon />}
              label="Twitter"
              size="medium"
              onClick={() => handleShareTo('twitter')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#1DA1F2',
                  color: 'white',
                },
              }}
            />
            <Chip
              icon={<WhatsAppIcon />}
              label="WhatsApp"
              size="medium"
              onClick={() => handleShareTo('whatsapp')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#25D366',
                  color: 'white',
                },
              }}
            />
            <Chip
              icon={<TelegramIcon />}
              label="Telegram"
              size="medium"
              onClick={() => handleShareTo('telegram')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#0088cc',
                  color: 'white',
                },
              }}
            />
            <Chip
              icon={<WeChatIcon />}
              label="微信"
              size="medium"
              onClick={() => handleShareTo('wechat')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#25D366',
                  color: 'white',
                },
              }}
            />
            <Chip
              icon={<QQIcon />}
              label="QQ"
              size="medium"
              onClick={() => handleShareTo('qq')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#12B7F5',
                  color: 'white',
                },
              }}
            />
            <Chip
              icon={<ZhihuIcon />}
              label="知乎"
              size="medium"
              onClick={() => handleShareTo('zhihu')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#0084FF',
                  color: 'white',
                },
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 分享说明 */}
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            分享功能说明：
          </Typography>
          <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>
              复制链接：将剧集链接复制到剪贴板
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              社交媒体分享：直接分享到常用社交平台
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              朋友可以通过链接直接访问剧集页面
            </li>
          </ul>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          关闭
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;